import React from 'react';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import usePermissions from '../../hooks/auth/usePermissions';
import useHistory from '../../hooks/navigation/useHistory';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import { CompanyOperationalStatus } from '../../services/exports/Constants';

export const DeliveryPaymentBlockReason = Object.freeze({
  GatewayNotVerified: 'gateway_not_verified',
  OnlinePaymentsOff: 'online_payments_off',
  StripeProcessingFeeMissing: 'stripe_processing_fee_missing',
});

/**
 * A courier provider's delivery is never paid in cash, so with online payments off or
 * the gateway unverified the shop hides delivery although it is switched on here. The
 * backend says why in `delivery_payment_block_reason`. An unverified gateway carries no
 * button: the KYC banner above already offers the verification step.
 */
export default function DeliveryPaymentBlockBanner() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Layout:DeliveryPaymentBlockBanner' });
  const { company } = useSelector((state) => state.currentCompany);
  const { canAccessScreen } = usePermissions();
  const history = useHistory();

  const reason = company?.delivery_payment_block_reason;

  if (!reason || company.operational_status === CompanyOperationalStatus.Suspended) {
    return null;
  }

  const canTurnOnOnlinePayments =
    reason === DeliveryPaymentBlockReason.OnlinePaymentsOff && canAccessScreen(ScreenEnum.OrderSettingsPaymentMethods);

  return (
    <Alert
      banner
      type="error"
      data-testid="delivery-payment-block-banner"
      title={
        <div className="flex items-center justify-between">
          <span>{t(reason)}</span>
          {canTurnOnOnlinePayments && (
            <Button type="link" onClick={() => history.push(ScreenEnum.OrderSettingsPaymentMethods)}>
              {t('turn_on_online_payments')}
            </Button>
          )}
        </div>
      }
    />
  );
}
